import type React from "react";
import {useCallback, useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";

import {usePlaylist} from "~/api/hooks/usePlaylist";
import {useUpdatePlaylist} from "~/api/hooks/useUpdatePlaylist";
import AppHeader from "~/component/common/AppHeader";

import PlaylistItem from "./PlaylistItem";
import ToggleFeaturedModalButton from "./buttons/ToggleFeaturedModalButton";
import DeletePlaylistModalButton from "./delete-playlist/DeletePlaylistModalButton";
import RenamePlaylistModalButton from "./rename-playlist/RenamePlaylistModalButton";

const ManageSinglePlaylist: React.FC = () => {
  const {playlistId} = useParams<{playlistId: string}>();
  const navigate = useNavigate();
  const {data: playlistData} = usePlaylist({id: playlistId});
  const [orderedLevelIds, setOrderedLevelIds] = useState<string[]>([]);

  useEffect(() => {
    if (playlistData?.levelIds) {
      setOrderedLevelIds(playlistData.levelIds);
    }

    // Redirect if the playlist is immutable
    if (playlistData?.isImmutable) {
      navigate(`/playlist/${playlistId}`);
    }
  }, [playlistData, playlistId, navigate]);

  // Hook to update the playlist document.
  const {mutate: updatePlaylist} = useUpdatePlaylist();

  // Helper function to update order on backend.
  const updateOrder = useCallback(
    (newOrder: string[]) => {
      setOrderedLevelIds(newOrder);
      if (playlistId) {
        updatePlaylist({
          data: {
            levelIds: newOrder,
            id: playlistId,
          },
        });
      }
    },
    [playlistId, updatePlaylist],
  );

  // Helper function to move an item in the array.
  const moveItem = useCallback(
    (fromIndex: number, toIndex: number) => {
      const newOrder = Array.from(orderedLevelIds);
      const [movedItem] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, movedItem!);
      updateOrder(newOrder);
    },
    [orderedLevelIds, updateOrder],
  );

  const handleMoveUp = useCallback(
    (index: number) => {
      if (index > 0) {
        moveItem(index, index - 1);
      }
    },
    [moveItem],
  );

  const handleMoveDown = useCallback(
    (index: number) => {
      if (index < orderedLevelIds.length - 1) {
        moveItem(index, index + 1);
      }
    },
    [orderedLevelIds, moveItem],
  );

  // Handler to delete the level from the playlist.
  const handleDeleteLevel = useCallback(
    (levelId: string) => {
      const newOrder = orderedLevelIds.filter((item) => item !== levelId);
      updateOrder(newOrder);
    },
    [orderedLevelIds, updateOrder],
  );

  return (
    <div className="flex flex-col w-screen min-h-screen overflow-auto relative text-white bg-transparent">
      <AppHeader
        title={playlistData?.name}
        parentPath={`/playlist/${playlistId}`}
      />

      <div className="fixed bottom-2 left-1/2 transform -translate-x-1/2 flex items-center space-x-4 z-50 pointer-events-auto">
        <RenamePlaylistModalButton />
        <DeletePlaylistModalButton />
        <ToggleFeaturedModalButton />
      </div>

      <main className="mt-16 px-2 pb-4 flex-1 z-20">
        <div>
          {orderedLevelIds.map((id, index) => (
            <PlaylistItem
              key={id}
              levelId={id}
              levelIds={orderedLevelIds}
              onMoveUp={() => handleMoveUp(index)}
              onMoveDown={() => handleMoveDown(index)}
              onDelete={() => handleDeleteLevel(id)}
            />
          ))}
        </div>
      </main>
    </div>
  );
};

export default ManageSinglePlaylist;
